<template>
	<div class="container">
		<div class="left">
			<div class="properties-menu">
				<PropertiesComponent />
			</div>
			<form @submit.prevent="register">
				<div class="logo-container">
					<img class="logo" src="@/assets/img/logo/logo.jpeg" alt="Logo" />
				</div>
				<div class="welcome-message">
					{{ $t('Register.welcome') }} <span class="highlight">{{ $t('APP_NAME') }}</span>
				</div>

				<label for="fullname">{{ $t('Register.fullName') }}</label>
				<input 
					type="text" 
					:placeholder="$t('Register.enterFullName')" 
					name="fullname" 
					v-model="fullName"
					:class="{ 'invalid-input': fullNameError }"
					required />
				<label for="email">{{ $t('Register.email') }}</label>

				<input 
					type="email" 
					:placeholder="$t('Register.enterEmail')" 
					name="email"
					v-model="email"
					:class="{ 'invalid-input': emailError }"
					required />
				<label for="password">{{ $t('Register.password') }}</label>
				<div class="password-input-container">
					<input
						ref="passwordInput"
						:type="showPassword ? 'text' : 'password'"
						:placeholder="$t('Register.enterPassword')"
						name="password"
						v-model="password" 
						@input="checkPassword"
						@focus="showPasswordRequirements = true"
						@blur="showPasswordRequirements = false"
						:class="{ 'invalid-input': password && !isPasswordValid }"
						required
					/>
					<span class="password-toggle" @click="togglePasswordVisibility">
						{{ showPassword ? '👁️' : '🙈' }}
					</span>
				</div>

				<div 
					v-if="showPasswordRequirements" 
					class="password-requirements"
					:class="{ 'show': showPasswordRequirements }"
				>
					<ul>
						<li :class="{ 'fulfilled': passwordLength }">
							<font-awesome-icon :icon="['fas', 'check-circle']" class="check-icon" />
							<span class="requirement">{{ $t('Register.passwordLength') }}</span>
						</li>
						<li :class="{ 'fulfilled': passwordUppercase }">
							<font-awesome-icon :icon="['fas', 'check-circle']" class="check-icon" />
							<span class="requirement">{{ $t('Register.passwordUppercase') }}</span>
						</li>
						<li :class="{ 'fulfilled': passwordLowercase }">
							<font-awesome-icon :icon="['fas', 'check-circle']" class="check-icon" />
							<span class="requirement">{{ $t('Register.passwordLowercase') }}</span>
						</li>
						<li :class="{ 'fulfilled': passwordSpecial }">
							<font-awesome-icon :icon="['fas', 'check-circle']" class="check-icon" />
							<span class="requirement">{{ $t('Register.passwordSpecial') }}</span>
						</li>
						<li :class="{ 'fulfilled': passwordNumber }">
							<font-awesome-icon :icon="['fas', 'check-circle']" class="check-icon" />
							<span class="requirement">{{ $t('Register.passwordNumber') }}</span>
						</li>
					</ul>
				</div>

				<label for="confirm-password">{{ $t('Register.confirmPassword') }}</label>
				<div class="password-input-container">
					<input
						:type="showConfirmPassword ? 'text' : 'password'"
						:placeholder="$t('Register.enterConfirmPassword')"
						name="confirm-password"
						v-model="confirmPassword"
						@input="checkConfirmPassword"
						:class="{ 'invalid-input': confirmPassword && (!isPasswordMatch || !isPasswordValid) }"
						required
					/>
					<span class="password-toggle" @click="toggleConfirmPasswordVisibility">
						{{ showConfirmPassword ? '👁️' : '🙈' }}
					</span>
				</div>
				<button 
					type="submit" 
					class="register-btn" 
					:disabled="!isFormValid || loading"
					>
					<span v-if="!loading">{{ $t('Register.name') }}</span>
					<span v-else class="spinner"></span>
				</button>
				<div class="login-link ">
					{{ $t('Register.alreadyHaveAccount') }} <a href="#" @click.prevent="goToLogin">{{ $t('Register.signIn') }}</a>
				</div>
			</form>
		</div>
		<div class="right">
			<div class="image-container">
				<img src="@/assets/img/auth/auth-register.png" alt="Auth Register Image" />
			</div>
		</div>
	</div>
</template>

<script>
import { register } from '@/api/auth.api';
import PropertiesComponent from '@/components/user/layout/PropertiesComponent.vue';

export default {
	name: "RegisterComponent",
	components: {
		PropertiesComponent
	},
	data() {
		return {
			showPassword: false,
			showConfirmPassword: false,
			darkMode: false,
			showTranslateMenu: false,
			password: '',
			confirmPassword: '',
			fullName: '',
			email: '',
			passwordLength: false,
			passwordUppercase: false,
			passwordLowercase: false,
			passwordSpecial: false,
			passwordNumber: false, 
			showPasswordRequirements: false,
			fullNameError: false,
			emailError: false,
			passwordError: false,
			confirmPasswordError: false,
			loading: false
		};
	},
	computed: {
		isPasswordMatch() {
			return this.password === this.confirmPassword;
		},
		isPasswordValid() {
			return (
				this.passwordLength &&
				this.passwordUppercase &&
				this.passwordLowercase &&
				this.passwordSpecial &&
				this.passwordNumber 
			);
		},
		isFormValid() {
			return (
				this.isPasswordMatch &&
				this.isPasswordValid &&
				this.fullName &&
				this.email
			);
		}
	},
	methods: {
		async register() {
			this.loading = true;
			this.fullNameError = !this.fullName;
			this.emailError = !this.email;
			this.passwordError = !this.password;
			this.confirmPasswordError = !this.confirmPassword;

			if (this.fullNameError || this.emailError || this.passwordError || this.confirmPasswordError) {
				this.loading = false; 
				return;
			}
			try {
				const response = await register(this.fullName, this.email, this.password);
				if (response.data.status) {
					this.goToLogin();
				} else {
					this.fullNameError = true;
					this.emailError = true;
				}
			} catch (error) {
				this.errorMessage = "Registration failed. Please try again later.";
			} finally {
				this.loading = false;
			}
		},
		togglePasswordVisibility() {
			this.showPassword = !this.showPassword;
		},
		toggleConfirmPasswordVisibility() {
			this.showConfirmPassword = !this.showConfirmPassword;
		},
		goToLogin() {
			this.$router.push({ name: 'Login' });
		},
		checkPassword() {
			this.passwordLength = this.password.length >= 8;
			this.passwordUppercase = /[A-Z]/.test(this.password);
			this.passwordLowercase = /[a-z]/.test(this.password);
			this.passwordSpecial = /[~`!@#$%^&*()\-_+={}[\]|\\;:"<>,./?]/.test(this.password);
			this.passwordNumber = /\d/.test(this.password);
		},
		checkConfirmPassword() {
			this.checkPassword();
		}
	}
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Quicksand:wght@300;400;500;600;700&display=swap");

*,
*::before,
*::after {
	margin: 0;
	padding: 0;
	box-sizing: border-box;
	font-family: "Quicksand", sans-serif;
}

.login-link {
  margin-top: 1.5rem;
  font-size: 0.9rem;
  color: #666;
}

.login-link a {
  color: var(--logo-color);
  font-weight: 600;
  text-decoration: none;
  position: relative;
}

.login-link a::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  width: 0;
  height: 2px;
  background: var(--logo-color);
  transition: width 0.3s ease;
}

.login-link a:hover::after {
  width: 100%;
}

.container {
	display: flex;
	width: 100%;
	height: 100%;
}

.left {
	flex: 0 0 31%;
	height: 100%;
	padding: 7%;
	display: flex;
	justify-content: center;
	align-items: center;
	background: white;
}

.right {
	flex: 1;
	height: 100%;
	display: flex;
	justify-content: center;
	align-items: center;
	background-color: #f4f5fa;
	border-radius: 25px;
	box-shadow: rgba(0, 0, 0, 0.24) 0px 3px 8px;
}

.right .image-container {
	text-align: center;
}

.right img {
	max-width: 60%;
	height: 60%;
}

.logo-container {
	display: flex;
	justify-content: center;
	align-items: center;
	margin-bottom: 32px;
	width: 100%;
}

.logo {
	width: 28%;
	display: flex;
	align-items: center;
	justify-content: center;
	background: white;
	border-radius: 30%;
	padding: 2px;
	margin: 8px;
}

.welcome-message {
	text-align: center;
	font-size: 1.4rem;
	margin-bottom: 32px;
}

.welcome-message .highlight {
	color: var(--logo-color);
}

.subtitle {
	text-align: left;
	font-size: 1rem;
	margin-bottom: 25px;
	/* Büyük bir alt boşluk */
}

/* Inputs and Labels */
input {
	width: 100%;
	padding: 12px 20px;
	border: 2px solid #ccc;
	outline: 0;
	border-radius: 5px;
	background-color: transparent;
	margin: 4px 0 18px;
	font-size: 0.85rem;
	font-weight: 600;
	transition: all 0.5s;
	position: relative;
}

input:focus {
	border: 2px solid #000;
}

input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus,
input:-webkit-autofill:active {
	-webkit-background-clip: text;
	-webkit-text-fill-color: #000;
	transition: background-color 5000s ease-in-out 0s;
}
.invalid-input {
	border-color: #e53e3e;
	/* Red border color for invalid input */
}

label {
	font-weight: 600;
	font-size: 0.9rem;
}

/* Register Button */
/* Register Button */
.register-btn {
	width: 100%;
	font-size: 0.9rem;
	font-weight: 600;
	padding: 12px 24px;
	margin: 12px 0 24px;
	border: 2px solid #6b7280;
	border-radius: 5px;
	background-color: white;
	cursor: pointer;
	color: black;
	transition: all 0.5s;
	display: flex;
	justify-content: center;
	align-items: center;
}

.register-btn:hover {
	background-color: var(--logo-color);
	border-color: var(--logo-color);
	color: #eee;
}


/* Other styles remain unchanged */

/* Sign in Message */
.signin-message {
	text-align: center;
	font-size: 0.9rem;
}

.signin-message a {
	color: var(--logo-color);
	text-decoration: none;
}

.signin-message a:hover {
	color: var (--logo-open-color);
}

/** Password Confirmation */
.password-requirements {
	margin-bottom: 10px;
	overflow: hidden;
	max-height: 0;
	transition: max-height 0.3s ease-out;
}

.password-requirements ul {
	list-style-type: none;
	padding-left: 0;
}

.password-requirements ul li {
	margin-bottom: 5px;
	display: flex;
	align-items: center;
	font-size: 14px;
	color: #333;
}

.password-requirements ul li .check-icon {
	margin-right: 5px;
	color: #ccc;
	/* Initially gray */
}

.password-requirements ul li.fulfilled .check-icon {
	color: #22c55e;
	/* Green when requirement is fulfilled */
}

.show {
	transition: 0.3s;
	max-height: 112px;
}

.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.3s;
}

.fade-enter,
.fade-leave-to {
	opacity: 0;
}

.password-toggle svg {
	font-size: 1rem;
}

/** Input Label */

.input-label {
	position: relative;
}

.input-label input {
	transition: border-color 0.2s;
}

.input-label input:focus {
	border-color: black;
}

.input-label p {
	color: black;
	user-select: none;
	position: absolute;
	top: 50%;
	transform: translateY(-50%);
	margin-left: 0.8rem;
	padding: 0 0.4rem;
	background: white;
	transition: top 0.2s, font-size 0.2s, color 0.2s;
}

.input-label .password-icon {
	display: flex;
	align-items: center;
	position: absolute;
	top: 50%;
	right: 1.2rem;
	transform: translateY(-50%);
	width: 1rem;
	color: black;
	transition: color 0.2s;
}

.input-label .password-icon .span{
	display: none;
}

.input-label .password-icon:hover {
	cursor: pointer;
}

.material-icons-outlined{
	color: black;
	font-size: 18px;
}

.input-label input:focus + p,
.input-label input:not(:placeholder-shown) + p {
	top: -11px;
	font-size: 0.9rem;
}

.input-label input:not(:focus) + p {
	color: #7a7a7a56;
}

@media (max-width: 768px) {
	.container {
		flex-direction: column;
	}

	.left {
		flex: 0 0 100%;
		position: fixed;
		padding: 13%;
	}

	.right {
		display: none;
	}
}
</style>
